// src/views/ReadHits.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { apiPath } from '../apiurl.js';
import LoadingSequence from '../assets/Loading/LoadingSequence';
import '../assets/css/ReadHits.css';

import { PreviewModal, SeqAlignModal } from '../utils/ReadHitsModals.js';
import BarChartComponent    from '../components/ReadhitComponent/BarChartComponent';
import ScatterPlotComponent from '../components/ReadhitComponent/ScatterPlotComponent';
import MapChartComponent    from '../components/ReadhitComponent/MapChartComponent';
import SequenceComponent    from '../components/ReadhitComponent/SequenceComponent';

// ── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZES = [25, 50, 100, 200];

const COLUMNS = [
  { key: 'qseqid',   label: 'qseqid'   },
  { key: 'sseqid',   label: 'sseqid'   },
  { key: 'pident',   label: 'pident',   sortable: true },
  { key: 'qlen',     label: 'qlen',     sortable: true },
  { key: 'len',      label: 'len',      sortable: true },
  { key: 'mm',       label: 'mm',       sortable: true },
  { key: 'gap',      label: 'gap',      sortable: true },
  { key: 'qstart',   label: 'qstart',   sortable: true },
  { key: 'qend',     label: 'qend',     sortable: true },
  { key: 'sstart',   label: 'sstart',   sortable: true },
  { key: 'send',     label: 'send',     sortable: true },
  { key: 'evalue',   label: 'evalue',   sortable: true },
  { key: 'bit',      label: 'bit',      sortable: true },
  { key: 'poltype',  label: 'poltype'   },
  { key: 'polstart', label: 'polstart', sortable: true },
  { key: 'polend',   label: 'polend',   sortable: true },
  { key: 'qseq',     label: 'qseq'     },
  { key: 'sseq',     label: 'sseq'     },
  { key: '__seqVis',  label: 'Seq Visual', type: 'seqVisButton'  },
  { key: '__preview', label: 'Preview',    type: 'previewButton' },
];

const MENU_ITEMS = [
  { key: 'DataTable',     label: 'Data Table',   icon: '⊞', desc: 'BLAST results table' },
  { key: 'Bar Charts',    label: 'Bar Charts',   icon: '▊', desc: 'Metric distributions' },
  { key: 'Scatter Plots', label: 'Scatter Plot', icon: '⊹', desc: 'Identity vs alignment length' },
  { key: 'Map Charts',    label: 'World Map',    icon: '◉', desc: 'Geographic origin of samples' },
  { key: 'Sequences',     label: 'Sequences',    icon: '🧬', desc: 'Sequence alignment viewer' },
];

const REGION_ORDER = ['5UTR','VP4','VP3','VP2','VP1','2A','2B','2C','3A','3B','3C','3D'];

const EXPORT_COLS = COLUMNS.filter(c => c.type !== 'seqVisButton' && c.type !== 'previewButton');

const exportCSV = (rows, filename) => {
  const header = EXPORT_COLS.map(c => `"${c.label}"`).join(',');
  const body = rows.map(row =>
    EXPORT_COLS.map(c => `"${String(row[c.key] ?? '').replace(/"/g, '""')}"`).join(',')
  ).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename + '.csv';
  a.click(); URL.revokeObjectURL(url);
};

const exportExcel = (rows, filename) => {
  const header = EXPORT_COLS.map(c => c.label);
  const data = rows.map(row => EXPORT_COLS.map(c => row[c.key] ?? ''));
  const ws = XLSX.utils.aoa_to_sheet([header, ...data]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'ReadHits');
  XLSX.writeFile(wb, filename + '.xlsx');
};

// ── Helpers ──────────────────────────────────────────────────────────────────

function parseSseqid(sseqid) {
  const parts = String(sseqid ?? '').split('_');
  return { region: parts[0] || '', pvtype: parts[1] || '' };
}

function safeNum(v) {
  if (v == null) return NaN;
  if (typeof v === 'bigint') return Number(v);
  const n = typeof v === 'number' ? v : parseFloat(v);
  return n;
}

function histBuckets(values, bucketCount = 20) {
  if (!values.length) return [];
  let lo = Infinity, hi = -Infinity;
  for (const v of values) { if (v < lo) lo = v; if (v > hi) hi = v; }
  if (lo === hi) return [{ label: String(lo.toFixed(1)), count: values.length }];
  const step = (hi - lo) / bucketCount;
  const buckets = Array.from({ length: bucketCount }, (_, i) => ({
    label: (lo + i * step).toFixed(1),
    lo: lo + i * step,
    hi: lo + (i + 1) * step,
    count: 0,
  }));
  for (const v of values) {
    const idx = Math.min(Math.floor((v - lo) / step), bucketCount - 1);
    buckets[idx].count++;
  }
  return buckets;
}

// localStorage cache for filter state per run
function loadCachedFilters(run) {
  try {
    const raw = localStorage.getItem(`rh_filters_${run}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function saveCachedFilters(run, state) {
  try { localStorage.setItem(`rh_filters_${run}`, JSON.stringify(state)); } catch {}
}

// ── Mini histogram chart ──────────────────────────────────────────────────────

const PLACEHOLDER_BUCKETS = [3,6,10,18,30,45,58,68,72,70,62,50,38,27,18,11,7,4,2,1];

function MiniHistogram({ buckets, color = '#6983f6', height = 52 }) {
  const [tipIndex, setTipIndex] = useState(null);

  if (!buckets.length) {
    const max = Math.max(...PLACEHOLDER_BUCKETS);
    return (
      <div style={{ width: '100%', height, display: 'flex', alignItems: 'flex-end', gap: '1px', opacity: 0.2 }}>
        {PLACEHOLDER_BUCKETS.map((v, i) => (
          <div key={i} style={{ flex: 1, height: `${(v / max) * 100}%`, background: color, borderRadius: '1px 1px 0 0' }} />
        ))}
      </div>
    );
  }

  const maxCount = Math.max(...buckets.map(b => b.count));
  const tip = tipIndex !== null ? buckets[tipIndex] : null;
  return (
    <div style={{ position: 'relative', width: '100%', height, display: 'flex', alignItems: 'flex-end', gap: '1px' }}>
      {buckets.map((b, i) => {
        const pct = maxCount > 0 ? Math.max((b.count / maxCount) * 100, 2) : 2;
        return (
          <div
            key={i}
            style={{
              flex: 1,
              height: `${pct}%`,
              background: color,
              borderRadius: '1px 1px 0 0',
              cursor: 'default',
              opacity: tipIndex !== null && i !== tipIndex ? 0.55 : 1,
              transition: 'opacity 0.1s',
            }}
            onMouseEnter={() => setTipIndex(i)}
            onMouseLeave={() => setTipIndex(null)}
          />
        );
      })}
      {tip && (
        <div
          className="rh-mini-tip"
          style={{
            position: 'absolute',
            top: 2,
            left: `${((tipIndex + 0.5) / buckets.length) * 100}%`,
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            zIndex: 100,
          }}
        >
          {tip.label}: {tip.count.toLocaleString()}
        </div>
      )}
    </div>
  );
}

// ── Facet row (region / pvtype) ───────────────────────────────────────────────

function FacetRow({ label, count, total, checked, onChange, color }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <label className={`rh-facet-row${checked ? ' checked' : ''}`}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span className="rh-facet-label">{label}</span>
      <span className="rh-facet-bar-wrap">
        <span className="rh-facet-bar" style={{ width: `${Math.max(pct, 1)}%`, background: color || 'var(--rh-accent)' }} />
      </span>
      <span className="rh-facet-count">{count.toLocaleString()}</span>
    </label>
  );
}

// ── Range input row ───────────────────────────────────────────────────────────

function RangeRow({ label, hint, minVal, maxVal, onMinChange, onMaxChange, minPlaceholder, maxPlaceholder }) {
  return (
    <div className="rh-filter-group">
      <div className="rh-filter-group-header">
        <span className="rh-filter-group-title">{label}</span>
        {hint && <span className="rh-filter-hint">{hint}</span>}
      </div>
      <div className="rh-range-row">
        <input
          className="rh-input rh-range-input"
          type="number"
          placeholder={minPlaceholder || 'Min'}
          value={minVal}
          onChange={e => onMinChange(e.target.value)}
        />
        <input
          className="rh-input rh-range-input"
          type="number"
          placeholder={maxPlaceholder || 'Max'}
          value={maxVal}
          onChange={e => onMaxChange(e.target.value)}
        />
      </div>
    </div>
  );
}

// ── Filter Panel ──────────────────────────────────────────────────────────────

const REGION_COLORS = {
  '5UTR': '#6366f1', 'VP1': '#2563eb', 'VP2': '#0891b2', 'VP3': '#0d9488',
  'VP4': '#059669',  '2A':  '#65a30d', '2B':  '#ca8a04', '2C':  '#ea580c',
  '3A':  '#dc2626',  '3B':  '#db2777', '3C':  '#9333ea', '3D':  '#7c3aed',
};
const PV_COLORS = { 'PV1': '#2563eb', 'PV2': '#16a34a', 'PV3': '#f59e0b' };
const DEFAULT_COLOR = '#6983f6';

function FilterPanel({
  stats, filters, dispatch, filteredCount, totalCount,
  regionSearch, setRegionSearch,
}) {
  const { regionCounts, pvtypeCounts, pidentRange, lenRange, evalueRange, bitRange,
          pidentBuckets, lenBuckets } = stats;

  const activeCount = (
    (filters.regions.length > 0 ? 1 : 0) +
    (filters.pvtypes.length > 0 ? 1 : 0) +
    (filters.minPident || filters.maxPident ? 1 : 0) +
    (filters.minLen    || filters.maxLen    ? 1 : 0) +
    (filters.maxEvalue ? 1 : 0) +
    (filters.minBit    ? 1 : 0)
  );

  const regionEntries = useMemo(() => {
    const entries = Object.entries(regionCounts)
      .sort((a, b) => {
        const oi = REGION_ORDER.indexOf(a[0]);
        const oj = REGION_ORDER.indexOf(b[0]);
        if (oi >= 0 && oj >= 0) return oi - oj;
        if (oi >= 0) return -1;
        if (oj >= 0) return 1;
        return b[1] - a[1];
      });
    const q = regionSearch.trim().toLowerCase();
    return q ? entries.filter(([k]) => k.toLowerCase().includes(q)) : entries;
  }, [regionCounts, regionSearch]);

  const pvEntries = useMemo(() =>
    Object.entries(pvtypeCounts).sort((a, b) => b[1] - a[1]),
    [pvtypeCounts]
  );

  const allRegionsChecked = filters.regions.length === 0;
  const allPvChecked      = filters.pvtypes.length === 0;

  const totalRegionCount = Object.values(regionCounts).reduce((s, v) => s + v, 0);
  const totalPvCount     = Object.values(pvtypeCounts).reduce((s, v) => s + v, 0);

  return (
    <div className="rh-filter-panel">

      {/* Panel header */}
      <div className="rh-filter-panel-header">
        <span className="rh-filter-panel-title">Filters</span>
        {activeCount > 0 && (
          <span className="rh-filter-active-badge">{activeCount} active</span>
        )}
        {activeCount > 0 && (
          <button className="rh-filter-reset-btn" onClick={() => dispatch({ type: 'RESET' })}>
            Reset all
          </button>
        )}
      </div>

      {/* Summary stats */}
      <div className="rh-filter-summary">
        <div className="rh-filter-summary-row">
          <span className="rh-filter-summary-label">Showing</span>
          <span className="rh-filter-summary-val">
            {filteredCount.toLocaleString()}
            <span className="rh-filter-summary-of"> / {totalCount.toLocaleString()}</span>
          </span>
        </div>
        <div className="rh-filter-summary-row">
          <span className="rh-filter-summary-label">Regions</span>
          <span className="rh-filter-summary-val">{Object.keys(regionCounts).length}</span>
        </div>
        <div className="rh-filter-summary-row">
          <span className="rh-filter-summary-label">PV types</span>
          <span className="rh-filter-summary-val">{Object.keys(pvtypeCounts).length}</span>
        </div>
      </div>

      <div className="rh-filter-scroll">

        {/* pident mini histogram */}
        <div style={{ marginTop: 0, paddingBottom: 8, height: '15%' }}>
        <div className="rh-filter-group">
          <div className="rh-filter-group-header">
            <span className="rh-filter-group-title">% Identity distribution</span>
            {pidentRange && (
              <span className="rh-filter-hint">{pidentRange.min.toFixed(1)}–{pidentRange.max.toFixed(1)}%</span>
            )}
          </div>
          <MiniHistogram buckets={pidentBuckets} color="#6983f6" height={82} />
        </div>
        </div>

        {/* Genomic Region */}
        <div className="rh-filter-group">
          <div className="rh-filter-group-header">
            <span className="rh-filter-group-title">Genomic Region</span>
            <span className="rh-filter-hint">{Object.keys(regionCounts).length} regions</span>
          </div>
          {Object.keys(regionCounts).length > 5 && (
            <div className="rh-facet-search-wrap">
              <input
                className="rh-facet-search"
                placeholder="Search regions…"
                value={regionSearch}
                onChange={e => setRegionSearch(e.target.value)}
              />
              {regionSearch && (
                <button className="rh-facet-search-clear" onClick={() => setRegionSearch('')}>✕</button>
              )}
            </div>
          )}
          <label className="rh-facet-row rh-facet-all">
            <input
              type="checkbox"
              checked={allRegionsChecked}
              onChange={() => dispatch({ type: 'SET_REGIONS', payload: [] })}
            />
            <span className="rh-facet-label">All regions</span>
            <span className="rh-facet-count">{totalRegionCount.toLocaleString()}</span>
          </label>
          <div className="rh-facet-list">
            {regionEntries.map(([region, count]) => (
              <FacetRow
                key={region}
                label={region}
                count={count}
                total={totalRegionCount}
                color={REGION_COLORS[region] || DEFAULT_COLOR}
                checked={filters.regions.includes(region)}
                onChange={() => dispatch({ type: 'TOGGLE_REGION', payload: region })}
              />
            ))}
          </div>
        </div>

        {/* PV Type */}
        {pvEntries.length > 0 && (
          <div className="rh-filter-group">
            <div className="rh-filter-group-header">
              <span className="rh-filter-group-title">Poliovirus Type</span>
            </div>
            <label className="rh-facet-row rh-facet-all">
              <input
                type="checkbox"
                checked={allPvChecked}
                onChange={() => dispatch({ type: 'SET_PVTYPES', payload: [] })}
              />
              <span className="rh-facet-label">All types</span>
              <span className="rh-facet-count">{totalPvCount.toLocaleString()}</span>
            </label>
            <div className="rh-facet-list">
              {pvEntries.map(([pvt, count]) => (
                <FacetRow
                  key={pvt}
                  label={pvt}
                  count={count}
                  total={totalPvCount}
                  color={PV_COLORS[pvt] || DEFAULT_COLOR}
                  checked={filters.pvtypes.includes(pvt)}
                  onChange={() => dispatch({ type: 'TOGGLE_PVTYPE', payload: pvt })}
                />
              ))}
            </div>
          </div>
        )}

        {/* % Identity range */}
        <RangeRow
          label="% Identity"
          hint={pidentRange ? `data: ${pidentRange.min.toFixed(1)}–${pidentRange.max.toFixed(1)}` : null}
          minVal={filters.minPident}
          maxVal={filters.maxPident}
          onMinChange={v => dispatch({ type: 'SET', field: 'minPident', value: v })}
          onMaxChange={v => dispatch({ type: 'SET', field: 'maxPident', value: v })}
          minPlaceholder="Min %"
          maxPlaceholder="Max %"
        />

        {/* Alignment Length */}
        <RangeRow
          label="Alignment Length (bp)"
          hint={lenRange ? `data: ${lenRange.min}–${lenRange.max}` : null}
          minVal={filters.minLen}
          maxVal={filters.maxLen}
          onMinChange={v => dispatch({ type: 'SET', field: 'minLen', value: v })}
          onMaxChange={v => dispatch({ type: 'SET', field: 'maxLen', value: v })}
          minPlaceholder="Min bp"
          maxPlaceholder="Max bp"
        />

        {/* Alignment length mini histogram */}
        <div style={{ marginTop: 16, paddingBottom: 8, height: '15%' }}>
          <div className="rh-filter-group">
            <div className="rh-filter-group-header">
              <span className="rh-filter-group-title">Alignment Len distribution</span>
              {lenRange && (
                <span className="rh-filter-hint">{lenRange.min}–{lenRange.max} bp</span>
              )}
            </div>
            <MiniHistogram buckets={lenBuckets} color="#0d9488" height={100} />
          </div>
        </div>

        {/* E-value */}
        <div className="rh-filter-group">
          <div className="rh-filter-group-header">
            <span className="rh-filter-group-title">E-value (max)</span>
            {evalueRange && (
              <span className="rh-filter-hint">lower = stricter</span>
            )}
          </div>
          <input
            className="rh-input"
            type="text"
            placeholder={evalueRange ? `e.g. 1e-10` : 'Max e-value'}
            value={filters.maxEvalue}
            onChange={e => dispatch({ type: 'SET', field: 'maxEvalue', value: e.target.value })}
          />
        </div>

        {/* Bit score */}
        <div className="rh-filter-group">
          <div className="rh-filter-group-header">
            <span className="rh-filter-group-title">Bit Score (min)</span>
            {bitRange && (
              <span className="rh-filter-hint">data: {bitRange.min.toFixed(0)}–{bitRange.max.toFixed(0)}</span>
            )}
          </div>
          <input
            className="rh-input"
            type="number"
            placeholder="Min bit score"
            value={filters.minBit}
            onChange={e => dispatch({ type: 'SET', field: 'minBit', value: e.target.value })}
          />
        </div>

      </div>
    </div>
  );
}

// ── Filter reducer ────────────────────────────────────────────────────────────

const EMPTY_FILTERS = {
  regions: [], pvtypes: [],
  minPident: '', maxPident: '',
  minLen: '', maxLen: '',
  maxEvalue: '', minBit: '',
};

function filterReducer(state, action) {
  switch (action.type) {
    case 'RESET':        return { ...EMPTY_FILTERS };
    case 'SET':          return { ...state, [action.field]: action.value };
    case 'SET_REGIONS':  return { ...state, regions: action.payload };
    case 'SET_PVTYPES':  return { ...state, pvtypes: action.payload };
    case 'TOGGLE_REGION':
      return {
        ...state,
        regions: state.regions.includes(action.payload)
          ? state.regions.filter(r => r !== action.payload)
          : [...state.regions, action.payload],
      };
    case 'TOGGLE_PVTYPE':
      return {
        ...state,
        pvtypes: state.pvtypes.includes(action.payload)
          ? state.pvtypes.filter(p => p !== action.payload)
          : [...state.pvtypes, action.payload],
      };
    case 'SEED': return { ...EMPTY_FILTERS, ...action.payload };
    default: return state;
  }
}

// ── Main component ────────────────────────────────────────────────────────────

const ReadHits = ({ isDashboard = false }) => {
  ReadHits.propTypes = { isDashboard: PropTypes.bool };

  const history  = useHistory();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const run = searchParams.get('run');

  // URL-param server filters (from LargeRunModal — seeded into client state on load)
  const urlMinPident  = searchParams.get('minPident')   || '';
  const urlMinLen     = searchParams.get('minLen')      || '';
  const urlPoltype    = searchParams.get('poltype')     || '';
  const urlSseqregion = searchParams.get('sseqregion')  || '';
  const hasUrlFilters = urlMinPident || urlMinLen || urlPoltype || urlSseqregion;

  // ── Server data state ───────────────────────────────────────────────────────
  const [data,       setData]      = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [truncated,  setTruncated]  = useState(false);

  // ── UI state ────────────────────────────────────────────────────────────────
  const [selectedMenu,     setSelectedMenu]     = useState('DataTable');
  const [previewRow,       setPreviewRow]       = useState(null);
  const [seqAlignRow,      setSeqAlignRow]      = useState(null);
  const [filterPanelOpen,  setFilterPanelOpen]  = useState(true);
  const [regionSearch,     setRegionSearch]     = useState('');

  // ── Table state ─────────────────────────────────────────────────────────────
  const [sortKey,  setSortKey]  = useState('');
  const [sortDir,  setSortDir]  = useState('asc');
  const [page,     setPage]     = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [search,   setSearch]   = useState('');

  // ── Client-side filter state ─────────────────────────────────────────────────
  const [filters, dispatch] = React.useReducer(filterReducer, EMPTY_FILTERS);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  // ── Fetch ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!run) return;
    setLoading(true);
    setFetchError(null);

    // Server filters — only used for initial cap when data > 75k
    const apiParams = {};
    if (urlMinPident)  apiParams.minPident  = urlMinPident;
    if (urlMinLen)     apiParams.minLen     = urlMinLen;
    if (urlPoltype)    apiParams.poltype    = urlPoltype;
    if (urlSseqregion) apiParams.sseqregion = urlSseqregion;

    axios
      .get(`${apiPath}/api/readhit/${run}`, { params: apiParams })
      .then((res) => {
        setData(res.data);
        setTruncated(res.headers['x-truncated'] === 'true');

        // Seed client filter state from URL params or localStorage
        const cached = loadCachedFilters(run);
        if (cached) {
          dispatch({ type: 'SEED', payload: cached });
        } else if (urlMinPident || urlSseqregion || urlPoltype) {
          dispatch({
            type: 'SEED',
            payload: {
              minPident: urlMinPident,
              regions:   urlSseqregion ? urlSseqregion.split(',').filter(Boolean) : [],
              pvtypes:   urlPoltype    ? urlPoltype.split(',').filter(Boolean)    : [],
            },
          });
        }
      })
      .catch((err) => {
        console.error('Error fetching ReadHit data:', err);
        setFetchError(err?.response?.data?.error ?? err?.message ?? 'Unknown error');
        setData([]);
      })
      .finally(() => setLoading(false));
  }, [run]); // eslint-disable-line

  // Persist filter state to localStorage when it changes
  useEffect(() => {
    if (run && data.length > 0) saveCachedFilters(run, filters);
  }, [filters, run, data.length]);

  // ── Computed stats from full dataset ────────────────────────────────────────
  const stats = useMemo(() => {
    const regionCounts  = {};
    const pvtypeCounts  = {};
    const pidentVals    = [];
    const lenVals       = [];
    const evalueVals    = [];
    const bitVals       = [];

    for (const row of data) {
      const { region, pvtype } = parseSseqid(row.sseqid);
      if (region) regionCounts[region]  = (regionCounts[region]  || 0) + 1;

      // pvtype from sseqid first, fallback to poltype field
      const pt = pvtype || String(row.poltype ?? '');
      if (pt) pvtypeCounts[pt] = (pvtypeCounts[pt] || 0) + 1;

      const p = safeNum(row.pident);  if (!isNaN(p)) pidentVals.push(p);
      const l = safeNum(row.len);     if (!isNaN(l) && isFinite(l)) lenVals.push(l);
      const e = safeNum(row.evalue);  if (!isNaN(e) && isFinite(e)) evalueVals.push(e);
      const b = safeNum(row.bit);     if (!isNaN(b)) bitVals.push(b);
    }

    const range = (arr) => arr.length
      ? { min: arr.reduce((m, v) => v < m ? v : m, arr[0]),
          max: arr.reduce((m, v) => v > m ? v : m, arr[0]) }
      : null;

    return {
      regionCounts,
      pvtypeCounts,
      pidentRange: range(pidentVals),
      lenRange:    range(lenVals),
      evalueRange: range(evalueVals),
      bitRange:    range(bitVals),
      pidentBuckets: histBuckets(pidentVals, 20),
      lenBuckets:    histBuckets(lenVals, 20),
    };
  }, [data]);

  // ── Client-side filtering ────────────────────────────────────────────────────
  const clientFiltered = useMemo(() => {
    const { regions, pvtypes, minPident, maxPident, minLen, maxLen, maxEvalue, minBit } = filters;
    const hasAny = regions.length || pvtypes.length || minPident || maxPident ||
                   minLen || maxLen || maxEvalue || minBit;
    if (!hasAny) return data;

    const mnP = minPident ? parseFloat(minPident) : -Infinity;
    const mxP = maxPident ? parseFloat(maxPident) :  Infinity;
    const mnL = minLen    ? Number(minLen)         : -Infinity;
    const mxL = maxLen    ? Number(maxLen)         :  Infinity;
    const mxE = maxEvalue ? parseFloat(maxEvalue)  :  Infinity;
    const mnB = minBit    ? parseFloat(minBit)     : -Infinity;

    return data.filter(row => {
      if (regions.length) {
        const r = String(row.sseqid ?? '').split('_')[0];
        if (!regions.includes(r)) return false;
      }
      if (pvtypes.length) {
        const pt = String(row.sseqid ?? '').split('_')[1] || String(row.poltype ?? '');
        if (!pvtypes.includes(pt)) return false;
      }
      const p = safeNum(row.pident);
      if (!isNaN(p) && (p < mnP || p > mxP)) return false;

      const l = safeNum(row.len);
      if (!isNaN(l) && (l < mnL || l > mxL)) return false;

      const e = safeNum(row.evalue);
      if (!isNaN(e) && e > mxE) return false;

      const b = safeNum(row.bit);
      if (!isNaN(b) && b < mnB) return false;

      return true;
    });
  }, [data, filters]);

  // ── Global text search on top of client filter ───────────────────────────────
  const textFiltered = useMemo(() => {
    if (!search.trim()) return clientFiltered;
    const q = search.toLowerCase();
    return clientFiltered.filter(row =>
      Object.values(row).some(v => String(v ?? '').toLowerCase().includes(q))
    );
  }, [clientFiltered, search]);

  // ── Sort ─────────────────────────────────────────────────────────────────────
  const sorted = useMemo(() => {
    if (!sortKey) return textFiltered;
    return [...textFiltered].sort((a, b) => {
      const av = a[sortKey] ?? ''; const bv = b[sortKey] ?? '';
      const an = parseFloat(av);  const bn = parseFloat(bv);
      const cmp = !isNaN(an) && !isNaN(bn)
        ? an - bn : String(av).localeCompare(String(bv));
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [textFiltered, sortKey, sortDir]);

  const totalCount  = sorted.length;
  const totalPages  = Math.max(1, Math.ceil(totalCount / pageSize));
  const paginated   = useMemo(
    () => sorted.slice(page * pageSize, (page + 1) * pageSize),
    [sorted, page, pageSize]
  );

  useEffect(() => setPage(0), [search, sortKey, sortDir, pageSize, filters]);

  // ── Pagination button list ───────────────────────────────────────────────────
  const pageButtons = useMemo(() => {
    const btns = []; const delta = 2;
    for (let i = 0; i < totalPages; i++) {
      if (i === 0 || i === totalPages - 1 || Math.abs(i - page) <= delta)
        btns.push(i);
      else if (btns[btns.length - 1] !== '…')
        btns.push('…');
    }
    return btns;
  }, [totalPages, page]);

  // ── Navigation ───────────────────────────────────────────────────────────────
  const handleBackClick = () => {
    const backParams = new URLSearchParams({ runId: run ?? '' });
    const urlTimeFrame = searchParams.get('timeFrame');
    if (urlTimeFrame) backParams.set('timeFrame', urlTimeFrame);
    history.push(`/pass/datatable?${backParams.toString()}`);
  };

  const handleSort = col => {
    if (!col.sortable) return;
    if (sortKey === col.key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(col.key); setSortDir('asc'); }
  };

  const thClass = col => {
    if (!col.sortable) return '';
    return sortKey === col.key ? `sortable sort-${sortDir}` : 'sortable';
  };

  const renderCell = (row, col) => {
    if (col.type === 'seqVisButton')
      return <button className="rh-btn-cell" onClick={() => setSeqAlignRow(row)}>⊞ Seq Visual</button>;
    if (col.type === 'previewButton')
      return <button className="rh-btn-cell" onClick={() => setPreviewRow(row)}>⊞ Preview</button>;
    const v = row[col.key];
    return (
      <span className="rh-mono-val" title={v == null || v === '' ? undefined : String(v)}>
        {v == null || v === '' ? '—' : String(v)}
      </span>
    );
  };

  const renderVisualization = () => {
    switch (selectedMenu) {
      case 'Bar Charts':    return <BarChartComponent    data={data} />;
      case 'Scatter Plots': return <ScatterPlotComponent data={data} />;
      case 'Map Charts':    return <MapChartComponent    data={data} />;
      case 'Sequences':     return <SequenceComponent    data={data} />;
      default:              return null;
    }
  };

  // ── Active filter count for topbar badge ─────────────────────────────────────
  const activeFilterCount = (
    (filters.regions.length > 0 ? 1 : 0) +
    (filters.pvtypes.length > 0 ? 1 : 0) +
    (filters.minPident || filters.maxPident ? 1 : 0) +
    (filters.minLen    || filters.maxLen    ? 1 : 0) +
    (filters.maxEvalue ? 1 : 0) +
    (filters.minBit    ? 1 : 0)
  );

  // ─────────────────────────────────────────────────────────────────────────────

  if (loading && data.length === 0) {
    return <LoadingSequence message="Loading ReadHit data" size="large" fullPage />;
  }

  return (
    <>
      <div className="rh-root">

        {/* ── TOP BAR ── */}
        <div className="rh-topbar">
          <div className="rh-topbar-left">
            <button className="rh-back-btn" onClick={handleBackClick}>← Back</button>
            <div className="rh-topbar-sep" />
            <div className="rh-run-badge">
              <span className="rh-run-label">RUN</span>
              <span className="rh-run-id">{run ?? '—'}</span>
            </div>
            {hasUrlFilters && !loading && (
              <>
                <div className="rh-topbar-sep" />
                <span className="rh-server-filter-tag">server-filtered</span>
              </>
            )}
          </div>
          <div className="rh-topbar-right">
            <div className="rh-topbar-stats">
              <div className="rh-topbar-stat">
                <span className="rh-topbar-stat-label">Loaded</span>
                <span className="rh-topbar-stat-val">
                  {loading ? '…' : data.length.toLocaleString()}
                </span>
              </div>
              {activeFilterCount > 0 && (
                <>
                  <div className="rh-topbar-sep" />
                  <div className="rh-topbar-stat">
                    <span className="rh-topbar-stat-label">Filtered</span>
                    <span className="rh-topbar-stat-val" style={{ color: 'var(--rh-accent)' }}>
                      {clientFiltered.length.toLocaleString()}
                    </span>
                  </div>
                </>
              )}
              <div className="rh-topbar-sep" />
              <div className="rh-topbar-stat">
                <span className={`rh-stat-dot${!loading ? ' live' : ''}`} />
                <span className="rh-topbar-stat-label">{loading ? 'Loading…' : 'Loaded'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── TRUNCATION BANNER ── */}
        {truncated && !loading && (
          <div className="rh-truncation-banner">
            <span className="rh-truncation-icon">⚠</span>
            <span>
              {hasUrlFilters
                ? <>Server filters applied — showing <strong>{data.length.toLocaleString()} rows</strong> (75,000 max). Use the filter panel to further narrow results, or go back and adjust server pre-filters.</>
                : <>This run has more than 75,000 alignment hits — showing the first <strong>75,000 rows</strong>. Use the filter panel to focus on a subset, or go back and apply server pre-filters.</>
              }
            </span>
            <button
              className="rh-filter-banner-clear"
              onClick={handleBackClick}
              style={{ marginLeft: 'auto' }}
            >
              ← Back to set pre-filters
            </button>
          </div>
        )}

        {/* ── BODY ── */}
        <div className="rh-body">

          {/* Left nav sidebar */}
          <nav className="rh-sidebar">
            <div className="rh-sidebar-section-label">Analysis Views</div>
            {MENU_ITEMS.map(item => (
              <button
                key={item.key}
                className={`rh-menu-item${selectedMenu === item.key ? ' active' : ''}`}
                onClick={() => setSelectedMenu(item.key)}
                title={item.desc}
              >
                <span className="rh-menu-icon">{item.icon}</span>
                {item.label}
                {item.key === 'DataTable' && activeFilterCount > 0 && (
                  <span className="rh-menu-filter-badge">{activeFilterCount}</span>
                )}
              </button>
            ))}
          </nav>

          {/* Main content */}
          <div className="rh-main">
            {selectedMenu !== 'DataTable' ? (
              <div style={{
                flex: 1, overflow: selectedMenu === 'Sequences' ? 'hidden' : 'auto',
                display: 'flex', flexDirection: 'column', minHeight: 0,
              }}>
                {renderVisualization()}
              </div>
            ) : (
              /* ── DataTable view: FilterPanel + TableArea ── */
              <div className="rh-datatable-layout">

                {/* Filter panel */}
                {filterPanelOpen && !loading && (
                  <FilterPanel
                    stats={stats}
                    filters={filters}
                    dispatch={dispatch}
                    filteredCount={clientFiltered.length}
                    totalCount={data.length}
                    regionSearch={regionSearch}
                    setRegionSearch={setRegionSearch}
                  />
                )}

                {/* Table area */}
                <div className="rh-table-area">

                  {/* Content header */}
                  <div className="rh-content-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {!loading && (
                        <button
                          className="rh-filter-toggle-btn"
                          onClick={() => setFilterPanelOpen(o => !o)}
                          title={filterPanelOpen ? 'Hide filters' : 'Show filters'}
                        >
                          {filterPanelOpen ? '◀ Filters' : '▶ Filters'}
                          {activeFilterCount > 0 && !filterPanelOpen && (
                            <span className="rh-filter-toggle-badge">{activeFilterCount}</span>
                          )}
                        </button>
                      )}
                      <span className="rh-section-title">BLAST Results</span>
                      {!loading && totalCount > 0 && (
                        <span className="rh-section-badge">
                          {totalCount.toLocaleString()} rows
                          {activeFilterCount > 0 && data.length !== totalCount && (
                            <span style={{ fontWeight: 400, opacity: 0.7 }}> of {data.length.toLocaleString()}</span>
                          )}
                        </span>
                      )}
                    </div>
                    {!loading && totalPages > 1 && (
                      <span style={{ fontSize: 11, color: 'var(--rh-text-faint)', fontFamily: 'var(--rh-mono)' }}>
                        Page {page + 1} / {totalPages}
                      </span>
                    )}
                  </div>

                  {/* Active filter tags strip */}
                  {activeFilterCount > 0 && !loading && (
                    <div className="rh-active-tags">
                      {filters.regions.length > 0 && filters.regions.map(r => (
                        <span key={r} className="rh-active-tag" style={{ background: REGION_COLORS[r] || 'var(--rh-accent)' }}>
                          {r}
                          <button onClick={() => dispatch({ type: 'TOGGLE_REGION', payload: r })}>✕</button>
                        </span>
                      ))}
                      {filters.pvtypes.length > 0 && filters.pvtypes.map(p => (
                        <span key={p} className="rh-active-tag" style={{ background: PV_COLORS[p] || 'var(--rh-accent)' }}>
                          {p}
                          <button onClick={() => dispatch({ type: 'TOGGLE_PVTYPE', payload: p })}>✕</button>
                        </span>
                      ))}
                      {(filters.minPident || filters.maxPident) && (
                        <span className="rh-active-tag">
                          pident {filters.minPident || '…'}–{filters.maxPident || '…'}%
                          <button onClick={() => { dispatch({ type: 'SET', field: 'minPident', value: '' }); dispatch({ type: 'SET', field: 'maxPident', value: '' }); }}>✕</button>
                        </span>
                      )}
                      {(filters.minLen || filters.maxLen) && (
                        <span className="rh-active-tag">
                          len {filters.minLen || '…'}–{filters.maxLen || '…'} bp
                          <button onClick={() => { dispatch({ type: 'SET', field: 'minLen', value: '' }); dispatch({ type: 'SET', field: 'maxLen', value: '' }); }}>✕</button>
                        </span>
                      )}
                      {filters.maxEvalue && (
                        <span className="rh-active-tag">
                          e-value ≤ {filters.maxEvalue}
                          <button onClick={() => dispatch({ type: 'SET', field: 'maxEvalue', value: '' })}>✕</button>
                        </span>
                      )}
                      {filters.minBit && (
                        <span className="rh-active-tag">
                          bit ≥ {filters.minBit}
                          <button onClick={() => dispatch({ type: 'SET', field: 'minBit', value: '' })}>✕</button>
                        </span>
                      )}
                      <button className="rh-active-tags-clear" onClick={() => dispatch({ type: 'RESET' })}>
                        Clear all
                      </button>
                    </div>
                  )}

                  {/* Controls bar */}
                  <div className="rh-controls">
                    <div className="rh-search-wrap">
                      <span className="rh-search-icon">⌕</span>
                      <input
                        className="rh-search-input"
                        placeholder="Search all columns…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                      />
                    </div>
                    {search && (
                      <button className="rh-btn-cell" onClick={() => setSearch('')}>✕ Clear</button>
                    )}
                    <div className="rh-controls-right">
                      <span className="rh-count-badge">Rows / page:</span>
                      <select
                        className="rh-page-size-select"
                        value={pageSize}
                        onChange={e => setPageSize(Number(e.target.value))}
                      >
                        {PAGE_SIZES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <div style={{ width: 1, height: 16, background: 'var(--rh-border, #e2e8f0)', margin: '0 4px' }} />
                      <button
                        className="rh-btn-cell"
                        disabled={textFiltered.length === 0}
                        onClick={() => exportCSV(textFiltered, `readhits_${run ?? 'data'}`)}
                        title={`Download all ${textFiltered.length.toLocaleString()} filtered rows as CSV`}
                      >⬇ CSV</button>
                      <button
                        className="rh-btn-cell"
                        disabled={textFiltered.length === 0}
                        onClick={() => exportExcel(textFiltered, `readhits_${run ?? 'data'}`)}
                        title={`Download all ${textFiltered.length.toLocaleString()} filtered rows as Excel (.xlsx)`}
                      >⬇ Excel</button>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="rh-table-wrap">
                    {loading ? (
                      <div className="rh-empty"><div className="rh-spinner" /><span>Fetching data…</span></div>
                    ) : fetchError ? (
                      <div className="rh-error" style={{ margin: 16 }}>
                        <strong>Failed to load data</strong>
                        <div style={{ marginTop: 6, fontSize: 11.5, fontFamily: 'var(--rh-mono)', wordBreak: 'break-all' }}>{fetchError}</div>
                      </div>
                    ) : paginated.length === 0 ? (
                      <div className="rh-empty">
                        <div style={{ fontSize: 34, opacity: 0.18 }}>◎</div>
                        <span>No rows match the current filters</span>
                        {activeFilterCount > 0 && (
                          <button className="rh-btn-cell" onClick={() => dispatch({ type: 'RESET' })}>
                            Reset filters
                          </button>
                        )}
                      </div>
                    ) : (
                      <table className="rh-table">
                        <thead>
                          <tr>
                            {COLUMNS.map(col => (
                              <th key={col.key} className={thClass(col)} onClick={() => handleSort(col)}>
                                {col.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {paginated.map((row, ri) => (
                            <tr key={row._id ?? row.qseqid ?? `row-${ri}`}>
                              {COLUMNS.map(col => <td key={col.key}>{renderCell(row, col)}</td>)}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>

                  {/* Pagination */}
                  {!loading && totalCount > 0 && (
                    <div className="rh-pagination">
                      <span>
                        Showing {(page * pageSize + 1).toLocaleString()}–
                        {Math.min((page + 1) * pageSize, totalCount).toLocaleString()} of{' '}
                        {totalCount.toLocaleString()} rows
                      </span>
                      <div className="rh-page-btns">
                        <button className="rh-page-btn" disabled={page === 0} onClick={() => setPage(0)}>«</button>
                        <button className="rh-page-btn" disabled={page === 0} onClick={() => setPage(p => p - 1)}>‹</button>
                        {pageButtons.map((b, i) =>
                          b === '…'
                            ? <span className="rh-page-ellipsis" key={`e${i}`}>…</span>
                            : <button key={b} className={`rh-page-btn${b === page ? ' current' : ''}`} onClick={() => setPage(b)}>{b + 1}</button>
                        )}
                        <button className="rh-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>›</button>
                        <button className="rh-page-btn" disabled={page >= totalPages - 1} onClick={() => setPage(totalPages - 1)}>»</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {previewRow  && <PreviewModal  row={previewRow}  onClose={() => setPreviewRow(null)}  />}
      {seqAlignRow && <SeqAlignModal row={seqAlignRow} onClose={() => setSeqAlignRow(null)} />}
    </>
  );
};

export default ReadHits;
